// Per-anchor role-list constants for Turbo Manufacturing.
//
// `MFG_DESK_ROLES` is the renamed-from-`MFG_ROLES` umbrella that gates
// the Manufacturing-bucket desk surfaces (Inventory / Models / Plants /
// Orders / Reports / Admin Home). Build pipeline (Kanban + Units roster)
// uses `MFG_BUILD_PIPELINE_ROLES`. Content is identical to the
// pre-2026-06-15 `MFG_ROLES` constant; the rename signals that this
// list gates the desk view specifically, NOT the role-scoped intake
// panel (that uses `MFG_ACCEPTER_ROLES`) and NOT the floor-side
// production surfaces (that will use `PRODUCTION_OPERATOR_ROLES` once
// the Production-bucket build kanban graduates from the `mfgFulfill`
// scaffold).
//
// Spec: docs/design/role_taxonomy.md §4 (token list) + §5.1 (umbrella
// implication map) + §6 (per-anchor module home).

/**
 * Manufacturing-bucket desk view gate. Reachable for `manufacturing`
 * umbrella holders + the platform escape hatches. Renamed from
 * `MFG_ROLES` to signal the surface it gates (desk view) rather than
 * the role-bearer class.
 */
export const MFG_DESK_ROLES: string[] = [
  "manufacturing",
  "platform_owner",
  "platform_admin",
  "platform_dev",
];

/**
 * Mfg-role variant of `salesIntake`: accept-to-pipeline / assign-plant
 * / materialize-unit view. A user holding ONLY `mfg_accepter` (no
 * `manufacturing` umbrella) sees the Mfg-role intake variant but NOT
 * the rest of the Manufacturing bucket — that's the new affordance the
 * Order Intake bucket move depends on.
 *
 * `manufacturing_order_processor` is the alternative spelling carried
 * over from `order_intake_pipeline.md` §10 + `turbo_manufacturing.md`
 * §11.8; the canonical token is `mfg_accepter` (per role-taxonomy
 * §5.4), but both spellings are accepted by the role matcher so a
 * pilot org can use either string in `user_roles.role` rows without
 * a backfill.
 */
export const MFG_ACCEPTER_ROLES: string[] = [
  "mfg_accepter",
  "manufacturing_order_processor",
  "manufacturing",
  "platform_owner",
  "platform_admin",
  "platform_dev",
];

/**
 * Production-bucket build-kanban + station-level tracking gate.
 * RESERVED for the Production graduation slice; today no consumer
 * references this constant. Exported so the graduation slice has a
 * stable home for its gate when it ships.
 *
 * The umbrella `manufacturing` token is included for backward-compat
 * — a current desk Mfg user keeps seeing the Production bucket after
 * it graduates. A `production_operator`-only user (no `manufacturing`)
 * is the new affordance: floor worker visibility into the Production
 * kanban without desk-side Mfg-bucket access.
 */
export const PRODUCTION_OPERATOR_ROLES: string[] = [
  "production_operator",
  "manufacturing",
  "platform_owner",
  "platform_admin",
  "platform_dev",
];

/** Parent role for shop-floor phase ACL (inventory_host_replacement_plan.md §5). */
export const MFG_PLANT_ROLE = "manufacturing_plant";

/** Supervisor override — may transition any phase. */
export const MFG_PLANT_SUPERVISOR_ROLE = "manufacturing_plant_supervisor";

/** Desk-lite plant manager — Production + Time Study + Build pipeline; no Pricing/Overhead/Admin. */
export const MFG_PLANT_MANAGER_ROLE = "manufacturing_plant_manager";

/**
 * Build-pipeline Kanban mode gate: desk manufacturing umbrella + plant
 * workers / supervisors / plant managers (managers see in-production units
 * only — Slice D).
 */
export const MFG_BUILD_PIPELINE_ROLES: string[] = [
  ...MFG_DESK_ROLES,
  MFG_PLANT_ROLE,
  MFG_PLANT_SUPERVISOR_ROLE,
  MFG_PLANT_MANAGER_ROLE,
];

/** QC seat — Builder Assistant plus the open-item inbox. Not the rest of Production. */
export const MFG_QC_ROLE = "mfg_qc";

/** Signs a finished inspection off, or sends items back as open failures. Not implied by regular QC. */
export const MFG_FINAL_QC_ROLE = "mfg_final_qc";

/** Production → Builder Assistant. Pipeline roles, plus QC seats. */
export const MFG_BUILDER_ASSISTANT_ROLES: string[] = [...MFG_BUILD_PIPELINE_ROLES, MFG_QC_ROLE, MFG_FINAL_QC_ROLE];

/** Mark a build-book candidate reviewed. Floor plant cannot review. */
export const MFG_BUILDER_ASSISTANT_REVIEW_ROLES: string[] = [
  ...MFG_DESK_ROLES,
  MFG_PLANT_SUPERVISOR_ROLE,
  MFG_PLANT_MANAGER_ROLE,
];

/**
 * Purchasing seat — Manufacturing → full Inventory + Models.
 * Includes desk umbrella for backward-compat; purchasing-only users do not
 * get Pricing / Overhead / Admin / other desk modes.
 */
export const MFG_PURCHASING_ROLE = "manufacturing_purchasing";

/**
 * Inventory + Models mode gate for purchasing + desk. Other desk modes stay
 * on `MFG_DESK_ROLES` only.
 */
export const MFG_PURCHASING_ROLES: string[] = [
  MFG_PURCHASING_ROLE,
  ...MFG_DESK_ROLES,
];

/**
 * Production → Purchasing (PO + invoice receipts + delivery confirm).
 * Desk + purchasing author POs; plant leads can confirm delivery.
 */
export const MFG_PURCHASING_MODE_ROLES: string[] = Array.from(
  new Set([
    ...MFG_PURCHASING_ROLES,
    MFG_PLANT_MANAGER_ROLE,
    MFG_PLANT_SUPERVISOR_ROLE,
  ]),
);

/**
 * Time Study mode visibility: desk, supervisors, plant managers, and
 * floor workers (workers are read-only — see WRITE roles below).
 */
export const MFG_TIME_STUDY_ROLES: string[] = [
  ...MFG_DESK_ROLES,
  MFG_PLANT_ROLE,
  MFG_PLANT_SUPERVISOR_ROLE,
  MFG_PLANT_MANAGER_ROLE,
];

/**
 * Time Study write (create/update/delete sessions & entries). Floor
 * `manufacturing_plant` is excluded — supervisors/managers/desk author.
 */
export const MFG_TIME_STUDY_WRITE_ROLES: string[] = [
  ...MFG_DESK_ROLES,
  MFG_PLANT_SUPERVISOR_ROLE,
  MFG_PLANT_MANAGER_ROLE,
];

/** Production modes plant managers may open (no desk Pricing/Overhead/Admin). */
export const MFG_PLANT_MANAGER_MODE_ROLES: string[] = [
  MFG_PLANT_MANAGER_ROLE,
  ...MFG_DESK_ROLES,
];

/**
 * Production → Inventory count mode visibility. Desk manufacturing +
 * purchasing (Bryan's count → PO path) + plant managers + supervisors.
 * Floor `manufacturing_plant` is excluded. Desk Inventory keeps
 * catalog/receive; this list gates the Production count surface only.
 */
export const MFG_INVENTORY_COUNT_ROLES: string[] = [
  ...MFG_DESK_ROLES,
  MFG_PURCHASING_ROLE,
  MFG_PLANT_MANAGER_ROLE,
  MFG_PLANT_SUPERVISOR_ROLE,
];

/**
 * BOM audit (recipe-abstract) — plant manager only for now (+ desk /
 * platform for parity). Floor plant + supervisor excluded.
 */
export const MFG_BOM_AUDIT_ROLES: string[] = [
  ...MFG_DESK_ROLES,
  MFG_PLANT_MANAGER_ROLE,
];

/**
 * Production → Audits mode (BOM audit + Time Study). Broader of the two
 * lane gates so floor plant can still open Time Study; BOM lane is
 * further gated inside the panel.
 */
export const MFG_AUDITS_ROLES: string[] = Array.from(
  new Set([...MFG_TIME_STUDY_ROLES, ...MFG_BOM_AUDIT_ROLES]),
);
